const MAPBOX_BASE_URL = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

function getToken(): string {
  return import.meta.env.VITE_MAPBOX_TOKEN ?? '';
}

export interface GeocodeResult {
  address: string;
  latitude: number;
  longitude: number;
  state?: string;
  county?: string;
}

export async function geocodeAddress(query: string): Promise<GeocodeResult[]> {
  if (!query.trim()) return [];
  const token = getToken();
  if (!token) {
    console.warn('VITE_MAPBOX_TOKEN not set — geocoding unavailable');
    return [];
  }

  const url = new URL(`${MAPBOX_BASE_URL}/${encodeURIComponent(query)}.json`);
  url.searchParams.set('access_token', token);
  url.searchParams.set('country', 'US');
  url.searchParams.set('types', 'address,place');
  url.searchParams.set('limit', '5');

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`Mapbox geocode error: ${response.status}`);

  const data = await response.json();

  return (data.features ?? []).map((f: MapboxFeature) => {
    const state = f.context?.find((c) => c.id.startsWith('region'))?.text;
    const county = f.context?.find((c) => c.id.startsWith('district'))?.text;
    return {
      address: f.place_name,
      latitude: f.center[1],
      longitude: f.center[0],
      state,
      county,
    };
  });
}

export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<GeocodeResult | null> {
  const token = getToken();
  if (!token) return null;

  const url = new URL(`${MAPBOX_BASE_URL}/${longitude},${latitude}.json`);
  url.searchParams.set('access_token', token);
  url.searchParams.set('types', 'address,place');
  url.searchParams.set('limit', '1');

  const response = await fetch(url.toString());
  if (!response.ok) return null;

  const data = await response.json();
  const f = data.features?.[0];
  if (!f) return null;

  const state = f.context?.find((c: MapboxContext) => c.id.startsWith('region'))?.text;
  const county = f.context?.find((c: MapboxContext) => c.id.startsWith('district'))?.text;

  return {
    address: f.place_name,
    latitude: f.center[1],
    longitude: f.center[0],
    state,
    county,
  };
}

interface MapboxFeature {
  place_name: string;
  center: [number, number];
  context?: MapboxContext[];
}

interface MapboxContext {
  id: string;
  text: string;
}